import React, { useMemo } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import RecapVideos from '../recap/RecapVideos';

const { FiExternalLink, FiFileText, FiInbox } = FiIcons;

const getImage = (item) => {
  const cuts = item?.image?.cuts || item?.photo?.cuts || [];
  return cuts.find((cut) => Number(cut.width) >= 600)?.src ||
    cuts[0]?.src ||
    item?.image?.src ||
    null;
};

const getArticleUrl = (item) => {
  if (item?.url) return item.url;
  if (item?.link) return item.link;
  if (item?.slug) return `https://www.mlb.com/news/${item.slug}`;
  return null;
};

const ArticleCard = ({ article }) => {
  const title = article.headline || article.title || 'MLB game story';
  const description = article.blurb || article.description || '';
  const image = getImage(article);
  const url = getArticleUrl(article);

  return (
    <article className="overflow-hidden border border-[#658d71] bg-[rgba(0,0,0,0.1)]">
      {image && (
        <img
          src={image}
          alt=""
          loading="lazy"
          className="aspect-video w-full object-cover"
        />
      )}
      <div className="flex flex-col gap-2 p-3">
        <h3 className="text-xs font-black uppercase leading-tight tracking-wide text-white">
          {title}
        </h3>
        {description && (
          <p className="line-clamp-3 text-[10px] leading-relaxed text-[#a9c9b1]">
            {description}
          </p>
        )}
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 self-start text-[9px] font-black uppercase tracking-widest text-[#d8ceb3] hover:text-white"
          >
            Read on MLB
            <SafeIcon icon={FiExternalLink} size={10} />
          </a>
        )}
      </div>
    </article>
  );
};

const GameNewsPanel = ({ content }) => {
  const articles = useMemo(() => {
    const editorial = content?.editorial || {};
    const candidates = [
      editorial.recap?.mlb,
      editorial.preview?.mlb,
      ...(editorial.articles?.items || [])
    ].filter(Boolean);

    const unique = new Map();
    candidates.forEach((article) => {
      const key = article.id || article.slug || article.headline || article.title;
      if (key) unique.set(String(key), article);
    });

    return [...unique.values()];
  }, [content]);

  const videoCount =
    content?.highlights?.highlights?.items?.filter(
      (item) => item.type === 'video' || item.playbacks?.length
    ).length || 0;

  if (!articles.length && !videoCount) {
    return (
      <div className="flex flex-col items-center gap-3 border border-[#658d71] py-14 text-center">
        <SafeIcon icon={FiInbox} size={28} className="text-[#658d71]" />
        <p className="text-xs font-bold uppercase tracking-widest text-[#a9c9b1]">
          No related stories or videos are available yet
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {videoCount > 0 && (
        <section>
          <h2 className="mb-3 text-xs font-black uppercase tracking-widest text-[#d8ceb3]">
            Game Videos
          </h2>
          <RecapVideos content={content} />
        </section>
      )}

      {articles.length > 0 && (
        <section>
          <div className="mb-3 flex items-center gap-2">
            <SafeIcon icon={FiFileText} size={13} className="text-[#d8ceb3]" />
            <h2 className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">
              Related News
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {articles.map((article, index) => (
              <ArticleCard
                key={article.id || article.slug || index}
                article={article}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default GameNewsPanel;